/** Manual sync cooldown (ms) — per feed or global tracked sync. */
export const FEED_SYNC_COOLDOWN_MS = 60 * 60 * 1000; // 60 minutes

const FEEDS_STORAGE_KEY = 'postsSection_feedLastSyncedAt';
const GLOBAL_STORAGE_KEY = 'postsSection_globalTrackedLastSyncedAt';

type TFeedSyncMap = Record<string, string>; // feedId → ISO timestamp

function readMap(): TFeedSyncMap {
  try {
    const raw = localStorage.getItem(FEEDS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(map: TFeedSyncMap) {
  try {
    localStorage.setItem(FEEDS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / private mode
  }
}

export function getFeedLastSyncedAt(feedId: string): Date | null {
  if (!feedId) return null;
  const iso = readMap()[feedId];
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
}

export function setFeedLastSyncedAt(feedId: string, at: Date = new Date()) {
  if (!feedId) return;
  const map = readMap();
  map[feedId] = at.toISOString();
  writeMap(map);
}

export function getFeedSyncCooldownRemainingMs(feedId: string, now: Date = new Date()): number {
  const last = getFeedLastSyncedAt(feedId);
  if (!last) return 0;
  const elapsed = now.getTime() - last.getTime();
  return Math.max(0, FEED_SYNC_COOLDOWN_MS - elapsed);
}

export function canSyncFeed(feedId: string, now: Date = new Date()): boolean {
  return getFeedSyncCooldownRemainingMs(feedId, now) <= 0;
}

export function getGlobalTrackedLastSyncedAt(): Date | null {
  try {
    const iso = localStorage.getItem(GLOBAL_STORAGE_KEY);
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isFinite(d.getTime()) ? d : null;
  } catch {
    return null;
  }
}

export function setGlobalTrackedLastSyncedAt(at: Date = new Date()) {
  try {
    localStorage.setItem(GLOBAL_STORAGE_KEY, at.toISOString());
  } catch {
    // ignore quota / private mode
  }
}

export function getGlobalTrackedSyncCooldownRemainingMs(now: Date = new Date()): number {
  const last = getGlobalTrackedLastSyncedAt();
  if (!last) return 0;
  const elapsed = now.getTime() - last.getTime();
  return Math.max(0, FEED_SYNC_COOLDOWN_MS - elapsed);
}

export function canSyncGlobalTracked(now: Date = new Date()): boolean {
  return getGlobalTrackedSyncCooldownRemainingMs(now) <= 0;
}

/** e.g. "42m" or "1h 5m" */
export function formatCooldownRemaining(ms: number): string {
  if (ms <= 0) return '';
  const totalMin = Math.ceil(ms / 60_000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}
